/**
 * HalPropValue parser for `cmd car_service get-property-value` output.
 *
 * Port of off-board/ctrl/pkg/vhal/adb_fallback.go — parseGetPropertyValue,
 * fillValue, formatSetData, and all helpers. The property-type bit constants
 * and regex are copied verbatim so fixtures from the Go test file pass 1:1.
 */

import { VhalPropertyStatus, type VhalPropertyValue } from "../types/vhal";

// ── Property-type bit constants (mirrors Go adb_fallback.go) ─────────────

const PROP_TYPE_MASK = 0x00ff0000;
const PROP_TYPE_STRING = 0x00100000;
const PROP_TYPE_BOOLEAN = 0x00200000;
const PROP_TYPE_INT32 = 0x00400000;
const PROP_TYPE_INT32VEC = 0x00410000;
const PROP_TYPE_INT64 = 0x00500000;
const PROP_TYPE_INT64VEC = 0x00510000;
const PROP_TYPE_FLOAT = 0x00600000;
const PROP_TYPE_FLOATVEC = 0x00610000;
// BYTES (0x00700000) and MIXED (0x00e00000) are unsupported — return error.

// ── Regex (verbatim from Go) ──────────────────────────────────────────────

const HAL_PROP_VALUE_RE =
  /HalPropValue\{.*ElapsedRealtimeNanos:\s*(\d+).*Status:\s*\w+\((0x[0-9a-fA-F]+)\),\s*Value:\s*(.*)\}/;

// ── Primitive parse helpers ───────────────────────────────────────────────

/** Strip an optional enum tag: "GEAR_PARK(0x4)" → "0x4", "3" → "3". */
function stripTag(tok: string): string {
  tok = tok.trim();
  const i = tok.lastIndexOf("(");
  if (i >= 0 && tok.endsWith(")")) return tok.slice(i + 1, -1);
  return tok;
}

/** Returns [digits, base] after stripping any enum tag. */
function splitBase(tok: string): [string, number] {
  const s = stripTag(tok);
  if (s.startsWith("0x") || s.startsWith("0X")) return [s.slice(2), 16];
  return [s, 10];
}

function parseInt32(tok: string): number {
  const [digits, base] = splitBase(tok);
  const v = parseInt(digits, base);
  if (isNaN(v)) throw new Error(`invalid int32: ${tok}`);
  return v | 0; // narrow to 32 bits (signed)
}

function parseInt64(tok: string): bigint {
  const [digits, base] = splitBase(tok);
  try {
    return BigInt(base === 16 ? "0x" + digits : digits);
  } catch {
    throw new Error(`invalid int64: ${tok}`);
  }
}

/** Parse leading numeric token of "17.0 CELSIUS" → 17.0. */
function parseFloatToken(tok: string): number {
  const fields = tok.trim().split(/\s+/);
  if (!fields.length || !fields[0]) throw new Error("empty float value");
  const v = parseFloat(fields[0]);
  if (isNaN(v)) throw new Error(`invalid float: ${tok}`);
  return v;
}

/** Strip "[…]" and split on commas; empty "[]" → []. */
function vectorElements(raw: string): string[] {
  raw = raw.trim().replace(/^\[/, "").replace(/\]$/, "").trim();
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim());
}

function parseInt32Vector(raw: string): number[] {
  return vectorElements(raw).map(parseInt32);
}

function parseInt64Vector(raw: string): bigint[] {
  return vectorElements(raw).map(parseInt64);
}

function parseFloatVector(raw: string): number[] {
  return vectorElements(raw).map(parseFloatToken);
}

// ── VhalPropertyValue shape produced by the parser ───────────────────────

export interface AdbParsedValue {
  propertyId: number;
  areaId: number;
  status: VhalPropertyStatus;
  timestamp: number;
  int32Values?: number[];
  int64Values?: bigint[];
  floatValues?: number[];
  stringValue?: string;
}

// ── Main parser ───────────────────────────────────────────────────────────

/**
 * Parse `cmd car_service get-property-value <propId> <areaId>` stdout into
 * an `AdbParsedValue`.  Returns `null` when output contains no HalPropValue
 * line (unsupported property, decode failure, empty output).
 *
 * Port of `parseGetPropertyValue` in adb_fallback.go.
 */
export function parseHalPropValue(
  out: string,
  propId: number,
  areaId: number,
): AdbParsedValue | null {
  // Pick the line for the requested area; fall back to first HalPropValue line.
  // Anchor the hex search to the "Area ID:" field so we don't false-match the
  // same hex in Status: or Value: (e.g. Status: AVAILABLE(0x0) always contains
  // (0x0), and Value: FUEL_TYPE_UNLEADED(0x1) would collide with area 0x1).
  let line = "";
  const areaTag = "Area ID:";
  const areaHex = `(0x${areaId.toString(16)})`;
  for (const l of out.split("\n")) {
    if (!l.includes("HalPropValue{")) continue;
    if (!line) line = l;
    const areaIdx = l.indexOf(areaTag);
    if (areaIdx >= 0 && l.slice(areaIdx).includes(areaHex)) {
      line = l;
      break;
    }
  }
  if (!line) return null;

  const m = HAL_PROP_VALUE_RE.exec(line);
  if (!m) return null;

  const tsNanos = parseInt(m[1]!, 10);
  const statusHex = parseInt(m[2]!.replace(/^0x/i, ""), 16);
  const rawValue = m[3]!.trim();

  const result: AdbParsedValue = {
    propertyId: propId,
    areaId,
    status: statusHex as VhalPropertyStatus,
    timestamp: tsNanos,
  };

  if (!fillValue(result, propId, rawValue)) return null;

  return result;
}

/**
 * Populate the typed field on `result` from the rendered value text.
 * Returns false when the type is unsupported (BYTES, MIXED) or unparseable.
 */
function fillValue(result: AdbParsedValue, propId: number, raw: string): boolean {
  const type = propId & PROP_TYPE_MASK;
  try {
    switch (type) {
      case PROP_TYPE_BOOLEAN:
        switch (raw.toUpperCase()) {
          case "TRUE":
            result.int32Values = [1];
            break;
          case "FALSE":
            result.int32Values = [0];
            break;
          default:
            return false;
        }
        break;

      case PROP_TYPE_INT32:
        result.int32Values = [parseInt32(raw)];
        break;

      case PROP_TYPE_INT32VEC:
        result.int32Values = parseInt32Vector(raw);
        break;

      case PROP_TYPE_INT64:
        result.int64Values = [parseInt64(raw)];
        break;

      case PROP_TYPE_INT64VEC:
        result.int64Values = parseInt64Vector(raw);
        break;

      case PROP_TYPE_FLOAT:
        result.floatValues = [parseFloatToken(raw)];
        break;

      case PROP_TYPE_FLOATVEC:
        result.floatValues = parseFloatVector(raw);
        break;

      case PROP_TYPE_STRING:
        result.stringValue = raw;
        break;

      default:
        // BYTES, MIXED, unknown — unsupported
        return false;
    }
    return true;
  } catch {
    return false;
  }
}

// ── formatSetData ─────────────────────────────────────────────────────────

/**
 * Format the `<data>` argument for `cmd car_service set-property-value`.
 *
 * Port of `formatSetData` in adb_fallback.go. Strings are shell-quoted so
 * values with spaces survive as a single argument.
 */
export function formatSetData(value: VhalPropertyValue): string | null {
  if (value.int32Values?.length) {
    return value.int32Values.map(String).join(",");
  }
  if (value.int64Values?.length) {
    return value.int64Values.map(String).join(",");
  }
  if (value.floatValues?.length) {
    return value.floatValues
      .map((f) => {
        // Match Go's strconv.FormatFloat(f, 'g', -1, 32) behaviour: drop
        // trailing zeros but keep the decimal when needed.
        const s = String(f);
        return s;
      })
      .join(",");
  }
  if (value.stringValue !== undefined && value.stringValue !== "") {
    // Shell-quote so spaces survive as one argument.
    return `"${value.stringValue.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  }
  return null; // no value field populated
}
